import { FormEvent, useEffect, useState } from "react";
// import { createUser, deleteUser, getAllUsers, getProfile } from "../service/AdminService";
import { createUser, getAllUsers, getProfile } from "../service/AdminService";
import { Role, UserProfile } from "../model/types";
import { Alert } from "../util/Alert";
import { Loading } from "../util/Loading";
import { extractErrorMessage } from "../service/api";

const emptyForm = {
  fullName: "",
  email: "",
  password: "",
  role: "CUSTOMER" as Role,
};

export const UsersPage = () => {
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [currentUser, setCurrentUser] = useState<UserProfile | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const loadUsers = async () => {
    setLoading(true);
    try {
      const [usersData, profileData] = await Promise.all([
        getAllUsers(),
        getProfile(),
      ]);
      setUsers(usersData);
      setCurrentUser(profileData);
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadUsers();
  }, []);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    try {
      await createUser(form);
      setForm(emptyForm);
      setMessage("User created");
      await loadUsers();
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  };

  // const handleDelete = async (userId: number) => {
  //   try {
  //     await deleteUser(userId);
  //     setMessage("User deleted");
  //     await loadUsers();
  //   } catch (err) {
  //     setError(extractErrorMessage(err));
  //   }
  // };

  if (loading) {
    return <Loading label="Loading users..." />;
  }

  return (
    <section className="grid gap-6 lg:grid-cols-[0.95fr_1.05fr]">
      <form
        onSubmit={handleSubmit}
        className="rounded-3xl border border-stone-200 bg-white p-6 shadow-sm"
      >
        <p className="text-xs uppercase tracking-[0.24em] text-emerald-700">
          User Management
        </p>
        <h2 className="mt-2 text-2xl font-semibold text-slate-900">
          Add user account
        </h2>
        <p className="mt-2 text-sm text-stone-600">
          Total registered users: {users.length}
        </p>
        <div className="mt-6 space-y-4">
          {message ? (
            <Alert type="success" message={message} onClose={() => setMessage("")} />
          ) : null}
          {error ? (
            <Alert type="failed" message={error} onClose={() => setError("")} />
          ) : null}
          <label className="block space-y-2 text-sm font-medium text-slate-700">
            <span>Full name</span>
            <input
              value={form.fullName}
              onChange={(event) =>
                setForm((prev) => ({ ...prev, fullName: event.target.value }))
              }
              placeholder="Enter full name"
              className="w-full rounded-2xl border border-stone-300 px-4 py-3"
              required
            />
          </label>
          <label className="block space-y-2 text-sm font-medium text-slate-700">
            <span>Email</span>
            <input
              type="email"
              value={form.email}
              onChange={(event) =>
                setForm((prev) => ({ ...prev, email: event.target.value }))
              }
              placeholder="Enter email address"
              className="w-full rounded-2xl border border-stone-300 px-4 py-3"
              required
            />
          </label>
          <label className="block space-y-2 text-sm font-medium text-slate-700">
            <span>Password</span>
            <input
              type="password"
              value={form.password}
              onChange={(event) =>
                setForm((prev) => ({ ...prev, password: event.target.value }))
              }
              placeholder="Enter password"
              className="w-full rounded-2xl border border-stone-300 px-4 py-3"
              required
            />
          </label>
          <label className="block space-y-2 text-sm font-medium text-slate-700">
            <span>Role</span>
            <select
              value={form.role}
              onChange={(event) =>
                setForm((prev) => ({
                  ...prev,
                  role: event.target.value as Role,
                }))
              }
              className="w-full rounded-2xl border border-stone-300 px-4 py-3"
            >
              <option value="CUSTOMER">CUSTOMER</option>
              <option value="ADMIN">ADMIN</option>
            </select>
          </label>
          <button
            type="submit"
            className="rounded-2xl bg-slate-900 px-4 py-3 text-sm font-semibold text-white"
          >
            Create user
          </button>
        </div>
      </form>

      <div className="rounded-3xl border border-stone-200 bg-white p-6 shadow-sm">
        <h3 className="text-xl font-semibold text-slate-900">Registered accounts</h3>
        <div className="mt-5 space-y-4">
          {users.map((user) => (
            <article
              key={user.id}
              className="rounded-2xl border border-stone-200 p-4"
            >
              <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div>
                  <div className="flex flex-wrap items-center gap-3">
                    <h4 className="text-lg font-semibold text-slate-900">
                      {user.fullName}
                    </h4>
                    <span className="rounded-full bg-stone-100 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-stone-700">
                      {user.role}
                    </span>
                    {currentUser?.id === user.id ? (
                      <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-emerald-700">
                        YOU
                      </span>
                    ) : null}
                  </div>
                  <p className="mt-2 text-sm text-stone-600">{user.email}</p>
                </div>
                {/* <button
                  type="button"
                  onClick={() => handleDelete(user.id)}
                  disabled={currentUser?.id === user.id}
                  className="rounded-2xl border border-rose-200 px-4 py-2 text-sm font-semibold text-rose-700 hover:bg-rose-50 disabled:cursor-not-allowed disabled:text-rose-300"
                >
                  Delete
                </button> */}
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};
